import React, { useCallback, useEffect, useState } from 'react'
import {
  GroupDTOFields,
  GroupInsertDTO,
  GroupUserFields,
  GroupUserViewDTO,
  GroupViewDTO
} from '../../../types/group/groupData'
import { Controller, useForm, useWatch } from 'react-hook-form'
import Grid from '@mui/material/Grid'
import { DataGrid, GridColDef, GridColumnMenuProps, GridRowSelectionModel } from '@mui/x-data-grid'
import { AsyncSelect, Button, ControlledCheckBox, ControlledTextField } from '../../../components/forms'
import { HttpCacheProfile } from '../../../http/cache/user/profile/httpCacheProfile'
import { HttpGroup } from '../../../http/group/httpGroup'
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, PaisLogoFields } from '../../../types'
import { HttpSystems } from 'src/http/workflow/httpSystems'
import { SistemaPrincipalFields } from 'src/types/workflow/cache'
import { Avatar, Divider, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { HttpCacheGrupo } from 'src/http/cache/httpCacheGrupo'
import { daysToNaturalLanguage } from "../../../utils/daysToNaturalLanguage";
import Box from "@mui/material/Box";
import Stack from "@mui/system/Stack";
import toast from "react-hot-toast";
import { z } from "zod";
import { GroupUserFilterDTOFields } from "../../../types/user/userData";
import { FilterAlt, FilterAltOff } from "@mui/icons-material";
import { ControlledAutocomplete } from "../../../components/forms/ControlledAutocomplete";
import { HttpCacheUser } from "../../../http/cache/user/httpCacheUser";
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'

type GroupForm = GroupInsertDTO & {
  codPais: number
  codSistema: number
}

interface GroupFormProps {
  group?: GroupViewDTO
  onAfterSubmit: () => void
}

const columnas: GridColDef<GroupUserViewDTO>[] = [
  {
    headerName: 'ID',
    field: GroupUserFields.UserId,
    flex: 1
  },
  {
    headerName: 'Cargo',
    field: GroupUserFields.Position,
    flex: 4
  },
  {
    headerName: 'Usuario',
    field: GroupUserFields.FullName,
    flex: 4
  },
  {
    headerName: 'Email',
    field: GroupUserFields.Mail,
    width: 200
  },

  {
    headerName: 'Último acceso',
    field: GroupUserFields.LastLoginDate,
    valueFormatter: ({ value }) => daysToNaturalLanguage(new Date(value)),
    valueGetter: ({ value }) => new Date(value),
    type: 'date',
    width: 200
  }
]

const schemaFiltroUsuarioGrupo = z.object({
  [GroupUserFilterDTOFields.CodsCargo]: z.array(z.number()).optional(),
  [GroupUserFilterDTOFields.Nombre]: z.string().optional(),
  [GroupUserFilterDTOFields.Mail]: z.string().optional(),
  [GroupUserFilterDTOFields.SoloActivos]: z.boolean().optional()
})

export type SchemaFiltroUsuarioGrupo = z.infer<typeof schemaFiltroUsuarioGrupo>

function GroupForm({ group, onAfterSubmit }: GroupFormProps) {
  const { getPaisLogoPorCodigo, paisLogos, loading } = usePaisLogo()

  const { control, handleSubmit, watch } = useForm<GroupForm>({
    defaultValues: {
      ...group
    }
  })

  const {
    control: controlFiltro,
    handleSubmit: handleSubmitFiltro,
    resetField: resetFieldFiltro,
  } = useForm<SchemaFiltroUsuarioGrupo>({
    defaultValues: {
      [GroupUserFilterDTOFields.SoloActivos]: true,
    }
  })

  const watchSoloActivos = useWatch({ control: controlFiltro, name: GroupUserFilterDTOFields.SoloActivos })

  useEffect(() => {
    handleSubmitFiltro(setFilterAndSearch)()
  }, [watchSoloActivos])

  const onSubmit = (data: GroupForm) => {
    const groupPromise = group
      ? () => HttpGroup.updateGroup(group[EntityWithIdFields.Id], data)
      : () => HttpGroup.insertGroup(data)

    const updateUsersPromise = group
      ? () => HttpGroup.updateGroupUsers(group[EntityWithIdFields.Id], (rowSelectionModel as number[]) || [])
      : () => Promise.resolve()

    const mensajeError = 'No se pudo guardar el grupo'
    const promise = new Promise<void>((resolve, reject) => {
      Promise.all([
        groupPromise(),
        updateUsersPromise()
      ]).then(() => {
        setTimeout(onAfterSubmit, 500)
        resolve()
      })
    })

    toast.promise(promise, {
      success: 'Guardado',
      error: () => mensajeError,
      loading: 'Guardando'
    })

    return promise
  }

  const opcionesSistemaPrincipal = useCallback(() =>

    Promise.resolve(paisLogos).then(res =>
      res.map(s => ({
        id: s.id,
        descripcion: s[EntityWithIdAndDescriptionFields.Description],
        icon: <CountryAvatar countryCode={s[PaisLogoFields.CodPais]} />
      }))
    ), [paisLogos])

  // HttpSystems.getSystemPrincipalsByUser().then(sis =>
  //   sis.map(opcion => ({
  //     id: opcion[EntityWithIdFields.Id],
  //     descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
  //     icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{height: '20px', width: '20px'}}/>
  //   }))
  // )

  const [users, setUsers] = useState<GroupUserViewDTO[]>()
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()

  useEffect(() => {
    setFilterAndSearch()
  }, [group])

  const setFilterAndSearch = (data?: SchemaFiltroUsuarioGrupo) => {
    return HttpGroup.getUsersByGroup(group?.[EntityWithIdFields.Id] || -1, data).then(response => {
      const disponibles = response.disponibles ?? []
      const asignados = response.asignados ?? []
      setUsers([...disponibles, ...asignados])
      const selectedGroups = asignados.map(user => user[GroupUserFields.UserId])
      setRowSelectionModel(prev => [...new Set([...selectedGroups, ...(prev || [])])])
    })
  }

  const onFilterChange = () => {
    handleSubmitFiltro(setFilterAndSearch)()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={'group-form'}>
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={6}>
          <ControlledTextField control={control} name={GroupDTOFields.Name} label='Nombre' fullWidth />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.ProfileId}
            label='Perfil'
            loadOptions={HttpCacheProfile.getProfileList}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodPais}
            fullWidth
            label='País'
            loadOptions={opcionesSistemaPrincipal}
          />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodSistema}
            label='Sistema'
            loadOptions={() => HttpCacheWorkflow.getSistemasPorSistemaPrincipal(watch('codPais'))}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.IdGrupoPadre}
            fullWidth
            label='Grupo coordinador'
            loadOptions={() => HttpGroup.getGruposCoordinadores(watch('codPais'), watch('codSistema'))}
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodTipoGrupo}
            fullWidth
            label='Tipo de grupo'
            loadOptions={HttpCacheGrupo.getGrupoTipos}
          />
        </Grid>

        <Grid item xs={6}>
          <ControlledTextField
            multiline
            rows={3}
            control={control}
            name={GroupDTOFields.Observations}
            label='Observaciones'
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={2}>

            <Stack direction={'row'} alignItems={'center'} spacing={4}>
              <Typography>Usuarios</Typography>
              <ControlledCheckBox
                label={'Solo Activos'}
                name={GroupUserFilterDTOFields.SoloActivos}
                control={controlFiltro} />
            </Stack>
            <Divider />
            <Box sx={{ height: '100%' }}>
              <DataGrid
                keepNonExistentRowsSelected
                hideFooterPagination
                columns={columnas}
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
                rows={users || []}
                getRowId={row => row[GroupUserFields.UserId]}
                density='compact'
                slots={{
                  columnMenu: (props: GridColumnMenuProps) => {
                    const {
                      colDef: { field }
                    } = props

                    if (field === GroupUserFields.FullName) {
                      return (
                        <Stack
                          direction='row'
                          spacing={2}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 2
                          }}
                        >
                          <Controller
                            control={controlFiltro}
                            name={GroupUserFilterDTOFields.Nombre}
                            render={({ field, fieldState }) => {
                              return (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                />
                              )
                            }}
                          />
                          <Button type='submit' onClick={onFilterChange}>
                            Buscar
                          </Button>
                          <Tooltip title='Limpiar filtro'>
                            <IconButton
                              onClick={() => {
                                resetFieldFiltro(GroupUserFilterDTOFields.Nombre)
                                onFilterChange() // search on filter reset
                              }}
                            >
                              <FilterAltOff />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )
                    }

                    if (field === GroupUserFields.Mail) {
                      return (
                        <Stack
                          direction='row'
                          spacing={2}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 2
                          }}
                        >
                          <Controller
                            control={controlFiltro}
                            name={GroupUserFilterDTOFields.Mail}
                            render={({ field, fieldState }) => {
                              return (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                />
                              )
                            }}
                          />
                          <Button type='submit' onClick={onFilterChange}>
                            Buscar
                          </Button>
                          <Tooltip title='Limpiar filtro'>
                            <IconButton
                              onClick={() => {
                                resetFieldFiltro(GroupUserFilterDTOFields.Mail)
                                onFilterChange() // search on filter reset
                              }}
                            >
                              <FilterAltOff />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )
                    }

                    if (field === GroupUserFields.Position) {
                      return (
                        <Stack
                          direction='row'
                          spacing={2}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 2
                          }}
                        >
                          <ControlledAutocomplete
                            control={controlFiltro}
                            name={GroupUserFilterDTOFields.CodsCargo}
                            valueField='id'
                            loadOptions={HttpCacheUser.getPositionList}
                            multiple
                          />
                          <Button type='submit' onClick={onFilterChange}>
                            Buscar
                          </Button>
                          <Tooltip title='Limpiar filtro'>
                            <IconButton
                              onClick={() => {
                                resetFieldFiltro(
                                  GroupUserFilterDTOFields.CodsCargo
                                )
                                onFilterChange() // search on filter reset
                              }}
                            >
                              <FilterAltOff />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )
                    }

                    return <></>
                  },
                  columnMenuIcon: FilterAlt
                }}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}

export default GroupForm
